import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Categories() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("out");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/categories");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/categories", { name: name.trim(), type, active: true });
      toast.success("Category added");
      setName(""); setType("out");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category? If it's in use it will be deactivated instead.")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Done");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <Link to="/settings" className="label-eyebrow hover:text-ink transition-colors">← Settings</Link>
        <h1 className="font-heading text-4xl font-bold mt-1">Categories</h1>
        <p className="text-sm text-muted mt-1">Tag each transaction with a category for clean reporting.</p>
      </div>

      <Card className="p-6 border border-line">
        <form onSubmit={add} className="flex items-end gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label className="label-eyebrow">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cleaning" required data-testid="category-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="label-eyebrow">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5 w-40" data-testid="category-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in">Cash In</SelectItem>
                <SelectItem value="out">Cash Out</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={submitting} data-testid="add-category-btn" className="bg-ink hover:bg-ink-soft text-white">
            <Plus size={16} className="mr-1.5" /> Add
          </Button>
        </form>
      </Card>

      <Card className="overflow-hidden border border-line">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-paper-alt border-b border-line">
                <th className="text-left px-4 py-3 label-eyebrow">Name</th>
                <th className="text-left px-4 py-3 label-eyebrow">Type</th>
                <th className="text-left px-4 py-3 label-eyebrow">Status</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody data-testid="categories-rows">
              {items.map((c) => (
                <tr key={c.id} className="border-b border-line hover:bg-paper/60">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-muted capitalize">{c.type}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-md ${c.active ? "bg-cashin-bg text-cashin" : "bg-paper-alt text-muted"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => del(c.id)} data-testid={`delete-category-${c.id}`} className="text-muted hover:text-cashout transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-muted text-sm">No categories yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
